import { useState } from "react";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from "lucide-react";

type TabKey = "nice-ta" | "screening" | "biosimilar" | "monitoring";

interface NiceTa {
  id: string;
  drug: string;
  indication: string;
  taRef: string;
  criteria: string[];
  restrictions: string;
  monitoring: string;
}

const NICE_TAS: NiceTa[] = [
  {
    id: "adalimumab-ra",
    drug: "Adalimumab (biosimilars available)",
    indication: "Rheumatoid Arthritis",
    taRef: "TA715 (2021)",
    criteria: [
      "DAS28 >5.1 (active RA) despite DMARD therapy",
      "Must have tried and failed ≥2 DMARDs (one of which must be MTX unless contraindicated)",
      "Assessment at 6 months — continue only if DAS28 improvement ≥1.2 from baseline",
      "Prescribe biosimilar at initiation unless clinically inappropriate",
    ],
    restrictions: "Biosimilars are mandatory at initiation. Prescribers should use the most cost-effective available biosimilar.",
    monitoring: "DAS28 at baseline and 6 months. Full blood count, LFTs, renal function as per BSR monitoring. Screen for latent TB, hepatitis B/C, varicella before starting.",
  },
  {
    id: "etanercept-ra",
    drug: "Etanercept (biosimilars available)",
    indication: "Rheumatoid Arthritis",
    taRef: "TA375 (2016, updated TA715)",
    criteria: [
      "DAS28 >5.1 despite adequate DMARD therapy",
      "Must have tried and failed ≥2 DMARDs including MTX (unless contraindicated)",
      "Continue only if DAS28 improves by ≥1.2 at 6-month review",
    ],
    restrictions: "Biosimilar preferred at initiation. Consider tolerability and patient preference if switching.",
    monitoring: "DAS28 at 0 and 6 months. Annual tuberculosis risk review. Suspend if serious infection.",
  },
  {
    id: "abatacept-ra",
    drug: "Abatacept",
    indication: "Rheumatoid Arthritis",
    taRef: "TA715 (2021)",
    criteria: [
      "DAS28 >5.1 despite ≥2 prior DMARDs",
      "Particularly consider for patients who have failed or are intolerant of anti-TNF therapy",
      "6-month review with DAS28 improvement ≥1.2 required for continuation",
    ],
    restrictions: "Indicated after inadequate response or intolerance to ≥1 anti-TNF. No biosimilar currently available.",
    monitoring: "Standard biologic monitoring. No additional special monitoring beyond BSR recommendations.",
  },
  {
    id: "secukinumab-pspa",
    drug: "Secukinumab",
    indication: "PsA and axSpA",
    taRef: "TA340 (PsA, 2015) / TA383 (axSpA, 2016)",
    criteria: [
      "PsA: active psoriatic arthritis (≥3 tender + ≥3 swollen joints) despite DMARD therapy",
      "axSpA: BASDAI ≥4 and spinal pain NRS ≥4 despite NSAID therapy (for nr-axSpA: objective signs of inflammation required)",
      "Continue only if BASDAI improves ≥50% or ≥2 units + spinal pain improves ≥1 unit at 12 weeks",
    ],
    restrictions: "Monoclonal antibody against IL-17A. Use with caution in IBD — may worsen Crohn's disease.",
    monitoring: "Baseline TB/hepatitis screen. Neutrophil count monitoring (may cause mild neutropenia). Candida infections: monitor and treat promptly.",
  },
  {
    id: "upadacitinib-ra",
    drug: "Upadacitinib (JAK inhibitor)",
    indication: "Rheumatoid Arthritis",
    taRef: "TA665 (2021)",
    criteria: [
      "Active RA in adults with DAS28 >5.1 who have had inadequate response or intolerance to ≥2 DMARDs",
      "Can be used as monotherapy or with MTX",
      "Continue only if DAS28 improvement ≥1.2 at 6 months",
    ],
    restrictions: "MHRA/NICE require cardiovascular risk, malignancy, and thromboembolism risk assessment before initiating JAK inhibitor. Prefer for patients without multiple CVD risk factors, aged ≥65, history of smoking, or VTE risk. Full MHRA safety review 2023 applies.",
    monitoring: "FBC, LFTs, lipids, creatinine at baseline and regularly. VTE risk assessment. MHRA 2023 restriction: do not use in patients at increased cardiovascular risk unless no suitable alternative.",
  },
  {
    id: "baricitinib-ra",
    drug: "Baricitinib (JAK inhibitor)",
    indication: "Rheumatoid Arthritis",
    taRef: "TA466 (2017, updated 2023)",
    criteria: [
      "Active RA with DAS28 >5.1 despite ≥2 DMARDs",
      "Can be used with or without MTX",
      "6-month review with DAS28 ≥1.2 improvement required",
    ],
    restrictions: "Same MHRA 2023 restrictions as other JAK inhibitors. Avoid in high-risk cardiovascular patients. Preferred 2 mg dose for patients aged ≥65. Avoid in those at increased risk of serious infections, thrombosis, or malignancy.",
    monitoring: "Same as upadacitinib. Particularly important: eGFR monitoring (dose-adjust at eGFR <60); lipid monitoring; LVEF if cardiac risk.",
  },
  {
    id: "belimumab-sle",
    drug: "Belimumab",
    indication: "Systemic Lupus Erythematosus",
    taRef: "TA483 (2017)",
    criteria: [
      "Active autoantibody-positive SLE (ANA or anti-dsDNA positive)",
      "High disease activity (SELENA-SLEDAI ≥8) despite standard therapy",
      "C3/C4 low OR high anti-dsDNA — at least one serological marker of activity",
      "Continue if SLEDAI improves ≥4 at 6 months and no new BILAG A or 2 new B organ domains",
    ],
    restrictions: "Licensed for SLE excluding active lupus nephritis or active CNS lupus. IV and SC formulations available.",
    monitoring: "Baseline TB, hepatitis. Watch for infusion reactions (IV). Monitor SLEDAI and serological markers. Discontinue if inadequate response at 6 months.",
  },
];

interface ScreeningItem {
  category: string;
  tests: { test: string; rationale: string; action: string }[];
}

const SCREENING: ScreeningItem[] = [
  {
    category: "Tuberculosis",
    tests: [
      {
        test: "Tuberculin skin test (TST) or IGRA (QuantiFERON-TB Gold / T-SPOT.TB)",
        rationale: "TNF inhibitors and JAK inhibitors reactivate latent TB. Anti-TNF agents carry the highest risk.",
        action: "LTBI: treat with 3 months isoniazid + rifampicin (or 6 months isoniazid alone) before starting biologic — usual minimum 1 month treatment before biologic if clinically urgent.",
      },
      {
        test: "Chest X-ray",
        rationale: "Identify active or previous TB, including apical changes, calcified lymph nodes.",
        action: "If abnormal: refer to TB service before starting biologic.",
      },
    ],
  },
  {
    category: "Hepatitis B",
    tests: [
      {
        test: "HBsAg, anti-HBs, anti-HBc",
        rationale: "Biologic therapy (especially rituximab, anti-TNF) can cause HBV reactivation — potentially fatal.",
        action: "HBsAg positive: refer to hepatology — do not start biologic without specialist input. Anti-HBc positive with negative HBsAg: discuss prophylaxis with hepatology (usually lamivudine or tenofovir).",
      },
    ],
  },
  {
    category: "Hepatitis C",
    tests: [
      {
        test: "Anti-HCV antibody (with reflex PCR if positive)",
        rationale: "Active HCV is a relative contraindication to some biologics; JAK inhibitors may worsen HCV.",
        action: "Active HCV: refer to hepatology for treatment (DAAs first). Anti-TNF can be used cautiously in treated/controlled HCV — discuss with hepatology.",
      },
    ],
  },
  {
    category: "Varicella",
    tests: [
      {
        test: "Varicella-zoster virus (VZV) IgG antibody",
        rationale: "Immunosuppression significantly increases risk of severe varicella and zoster.",
        action: "If seronegative and no previous vaccination: administer live VZV vaccine (Varivax/Zostavax). Must wait ≥4 weeks after live vaccine before starting immunosuppressive biologic.",
      },
    ],
  },
  {
    category: "HIV",
    tests: [
      {
        test: "HIV 1/2 Ag/Ab combination assay",
        rationale: "HIV is a relative contraindication to several biologics. Untreated HIV significantly complicates biologic therapy.",
        action: "HIV positive: refer to HIV/ID team. Biologic may be possible if on effective ART with undetectable viral load — specialist MDT decision.",
      },
    ],
  },
  {
    category: "Vaccinations (pre-biologic checklist)",
    tests: [
      {
        test: "Pneumococcal vaccine (PCV20 or PCV13 + PPSV23)",
        rationale: "Immunosuppression markedly increases pneumococcal disease risk.",
        action: "Administer before starting immunosuppressive therapy. Follow NHSE Green Book schedule.",
      },
      {
        test: "Annual influenza (inactivated)",
        rationale: "Influenza carries significant morbidity in immunosuppressed patients.",
        action: "Recommend annually — safe to give while on biologic (inactivated vaccine).",
      },
      {
        test: "Shingles vaccine (Shingrix — non-live)",
        rationale: "Zoster risk is significantly elevated on JAK inhibitors and high-dose steroids.",
        action: "Administer Shingrix (non-live) before or after starting biologics. JCVI 2023 recommends for patients on immunosuppression.",
      },
      {
        test: "COVID-19 vaccination",
        rationale: "Severe COVID risk significantly increased on immunosuppressives.",
        action: "Ensure up-to-date per NHSE seasonal schedule. Hold MTX for 2 weeks post-booster where possible (BSR 2022 guidance).",
      },
    ],
  },
];

interface BiosimilarEntry {
  drug: string;
  originator: string;
  biosimilars: string[];
  switchingGuidance: string;
  note: string;
}

const BIOSIMILARS: BiosimilarEntry[] = [
  {
    drug: "Adalimumab",
    originator: "Humira (AbbVie)",
    biosimilars: ["Amgevita", "Hyrimoz", "Imraldi", "Idacio", "Hadlima", "Yuflyma", "Libmyris"],
    switchingGuidance: "BSR 2022: single non-medical switch is acceptable and safe. Multiple switches require monitoring but evidence supports safety. Patient consent and communication are essential.",
    note: "Humira is high-concentration (100 mg/mL) citrate-free — some biosimilars are also available in this formulation, reducing injection site pain.",
  },
  {
    drug: "Etanercept",
    originator: "Enbrel (Pfizer)",
    biosimilars: ["Benepali", "Erelzi", "Nepexto"],
    switchingGuidance: "Switching from originator to biosimilar is supported by NICE and BSR. Switch should be communicated to patient and documented.",
    note: "All biosimilars are equivalent in efficacy and safety based on regulatory approval evidence.",
  },
  {
    drug: "Infliximab",
    originator: "Remicade (J&J)",
    biosimilars: ["Inflectra", "Flixabi", "Zessly", "Remsima (IV and SC)"],
    switchingGuidance: "Multiple switches demonstrated safe in NORSWITCH and NOR-DRUM studies. NHS England biosimilar framework applies.",
    note: "Remsima SC (subcutaneous) is now approved for RA, AS, and PsA — may be preferred for patient convenience and cost.",
  },
  {
    drug: "Rituximab",
    originator: "MabThera / Rituxan (Roche/Genentech)",
    biosimilars: ["Rixathon", "Riximyo", "Truxima"],
    switchingGuidance: "BSR guidance supports switch to rituximab biosimilar — no loss of efficacy demonstrated. Applies to RA, vasculitis, and other indications.",
    note: "Rituximab is a CD20 B-cell depleting agent — not a typical anti-TNF. Immunoglobulin monitoring recommended on long-term therapy.",
  },
  {
    drug: "Abatacept",
    originator: "Orencia (Bristol-Myers Squibb)",
    biosimilars: ["No biosimilar currently approved in UK (as of 2025)"],
    switchingGuidance: "No non-medical switching applicable. Continue originator.",
    note: "Patent expiry expected 2026 — biosimilars in development.",
  },
];

interface MonitoringProtocol {
  drug: string;
  drugClass: string;
  baseline: string[];
  during: string[];
  frequency: string;
  suspend: string[];
}

const MONITORING: MonitoringProtocol[] = [
  {
    drug: "Methotrexate (cDMARD)",
    drugClass: "cDMARD",
    baseline: ["FBC", "U&E", "LFTs", "Albumin", "CXR (if respiratory symptoms)", "Hepatitis B/C serology"],
    during: ["FBC, U&E, LFTs"],
    frequency: "Every 4 weeks until stable dose for 6 weeks, then every 12 weeks",
    suspend: ["WBC <3.5 × 10⁹/L", "Neutrophils <2 × 10⁹/L", "Platelets <150 × 10⁹/L", "AST/ALT >2× ULN", "Albumin <30 g/L (in absence of active disease)", "Creatinine rise >30%"],
  },
  {
    drug: "Hydroxychloroquine (cDMARD)",
    drugClass: "cDMARD",
    baseline: ["FBC", "Renal function", "Baseline visual assessment (if pre-existing retinopathy risk)", "G6PD if relevant"],
    during: ["Annual retinal screening (OCT and visual fields) after 5 years — or earlier if risk factors: renal impairment, >5 mg/kg IBW/day, concurrent tamoxifen"],
    frequency: "Annual retinal monitoring after 5 years (RCOphth 2020 guideline). FBC and U&E every 6–12 months",
    suspend: ["Visual symptoms — stop and refer urgently", "Confirmed retinopathy (irreversible)"],
  },
  {
    drug: "Anti-TNF agents (bDMARD)",
    drugClass: "bDMARD",
    baseline: ["FBC, LFTs, U&E", "CRP/ESR", "Hepatitis B/C", "HIV", "TB screening (IGRA + CXR)", "VZV serology if uncertain", "Varicella/HZV vaccination if needed"],
    during: ["FBC, LFTs, CRP at 3 months, 6 months, then every 6 months", "TB risk reassessment annually"],
    frequency: "BSR shared care protocol: 3/12, 6/12 then 6-monthly",
    suspend: ["Serious infection (suspend until resolved)", "Active TB", "Demyelinating disease", "New hepatitis B infection", "Cardiac failure NYHA III/IV (CHF)", "HBsAg positive — specialist review"],
  },
  {
    drug: "JAK Inhibitors (tsDMARD)",
    drugClass: "tsDMARD",
    baseline: ["FBC, LFTs, U&E, lipids, CRP", "VTE risk assessment (NICE and MHRA 2023)", "Cardiovascular risk assessment", "Hepatitis B/C, HIV, TB", "Shingrix vaccination (non-live) pre-treatment"],
    during: ["FBC, LFTs, U&E, lipids at 4–8 weeks then every 3 months for 1 year, then 6-monthly", "VTE vigilance throughout"],
    frequency: "MHRA 2023 restrictions: highest risk CVD patients — avoid unless no suitable alternative",
    suspend: ["Hb <80 g/L", "ANC <1 × 10⁹/L", "Platelets <50 × 10⁹/L", "Active serious infection", "VTE event", "Confirmed malignancy (case-by-case)"],
  },
  {
    drug: "Rituximab (CD20 B-cell depletion)",
    drugClass: "bDMARD",
    baseline: ["FBC, Ig levels (IgG, IgM, IgA)", "LFTs, U&E", "Hepatitis B/C (HBsAg, anti-HBs, anti-HBc)", "TB screening", "VZV, varicella vaccination if needed"],
    during: ["FBC before each cycle", "Immunoglobulins (IgG) every 6 months — if IgG <6 g/L discuss with immunology/haematology", "Hepatitis B reactivation monitoring"],
    frequency: "BSR protocol: FBC before each cycle (typically 6-monthly). Ig levels 6-monthly.",
    suspend: ["Active serious infection", "HBV reactivation", "IgG <4 g/L — discuss IVIG with immunology", "PML (JC virus) — urgent neurology referral"],
  },
];

function NiceTaSection() {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 mb-4">
        NICE Technology Appraisal prescribing criteria for biologic and targeted synthetic DMARDs. All biologic prescribing in RA requires DAS28 &gt;5.1 as the standard NICE threshold unless specified.
      </p>
      {NICE_TAS.map((ta) => (
        <Card key={ta.id} className="overflow-hidden">
          <button className="w-full text-left" aria-expanded={open === ta.id} onClick={() => setOpen(open === ta.id ? null : ta.id)}>
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <CardTitle className="text-sm">{ta.drug}</CardTitle>
                  <Badge variant="secondary" className="text-xs">{ta.taRef}</Badge>
                  <Badge className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-50">{ta.indication}</Badge>
                </div>
              </div>
              {open === ta.id ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
            </CardHeader>
          </button>
          {open === ta.id && (
            <CardContent className="pt-0 space-y-3">
              <div>
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">NICE Criteria</p>
                <ul className="space-y-1.5">
                  {ta.criteria.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">Prescribing restriction</p>
                <p className="text-xs text-amber-700">{ta.restrictions}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-1">Monitoring summary</p>
                <p className="text-xs text-slate-600">{ta.monitoring}</p>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

function ScreeningSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 mb-4">
        Pre-treatment screening requirements before initiating biologic or tsDMARD therapy. Based on BSR and NICE guidance.
      </p>
      {SCREENING.map((screen, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{screen.category}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {screen.tests.map((t, j) => (
              <div key={j} className="border border-slate-100 rounded-xl p-3 space-y-2">
                <p className="text-sm font-medium text-slate-800">{t.test}</p>
                <p className="text-xs text-slate-500">{t.rationale}</p>
                <div className="bg-indigo-50 rounded-lg p-2">
                  <p className="text-xs font-semibold text-indigo-700 mb-0.5">Action</p>
                  <p className="text-xs text-indigo-600">{t.action}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function BiosimilarSection() {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 mb-4">
        <strong>BSR/NICE position (2022–2024):</strong> Non-medical switching to biosimilar at initiation is mandatory per NICE. Single non-medical switch is safe and supported. Multiple switches are generally safe but patient communication is essential. Prescribers should use the most cost-effective available biosimilar.
      </div>
      {BIOSIMILARS.map((b, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-sm">{b.drug}</CardTitle>
              <Badge variant="secondary" className="text-xs">{b.originator}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">UK-approved biosimilars</p>
              <div className="flex flex-wrap gap-2">
                {b.biosimilars.map((name, j) => (
                  <span key={j} className="bg-indigo-50 text-indigo-700 text-xs px-2 py-0.5 rounded-full border border-indigo-100">{name}</span>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-slate-700 mb-1">Switching guidance</p>
              <p className="text-xs text-slate-600">{b.switchingGuidance}</p>
            </div>
            {b.note && (
              <p className="text-xs text-slate-500 italic">{b.note}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MonitoringSection() {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 mb-4">
        Prescriber-level monitoring schedules based on BSR shared care and NICE guidance. Suspension thresholds are indicative — clinical judgment applies.
      </p>
      {MONITORING.map((m, i) => (
        <Card key={i} className="overflow-hidden">
          <button className="w-full text-left" aria-expanded={open === m.drug} onClick={() => setOpen(open === m.drug ? null : m.drug)}>
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-sm">{m.drug}</CardTitle>
                  <Badge variant="secondary" className="text-xs">{m.drugClass}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">{m.frequency}</p>
              </div>
              {open === m.drug ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
            </CardHeader>
          </button>
          {open === m.drug && (
            <CardContent className="pt-0 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Baseline investigations</p>
                  <ul className="space-y-1">
                    {m.baseline.map((b, j) => <li key={j} className="text-xs text-slate-600 flex items-start gap-1"><span className="text-indigo-400">•</span>{b}</li>)}
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Ongoing monitoring</p>
                  <ul className="space-y-1">
                    {m.during.map((d, j) => <li key={j} className="text-xs text-slate-600 flex items-start gap-1"><span className="text-indigo-400">•</span>{d}</li>)}
                  </ul>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <p className="text-xs font-semibold text-red-700">Suspend / withhold thresholds</p>
                </div>
                <ul className="space-y-1">
                  {m.suspend.map((s, j) => <li key={j} className="text-xs text-red-600 flex items-start gap-1"><span>•</span>{s}</li>)}
                </ul>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

const TABS: { key: TabKey; label: string }[] = [
  { key: "nice-ta", label: "NICE TA Criteria" },
  { key: "screening", label: "Pre-treatment Screening" },
  { key: "biosimilar", label: "Biosimilar Guidance" },
  { key: "monitoring", label: "Monitoring Schedules" },
];

export default function Prescribing() {
  const [tab, setTab] = useState<TabKey>("nice-ta");

  return (
    <DoctorLayout title="Prescribing Reference">
      <div className="mb-6">
        <p className="text-slate-600 mb-4">
          Consultant-level prescribing reference for rheumatology biologics and DMARDs. Based on NICE Technology Appraisals, BSR guidelines, and MHRA guidance. Refer to the BNF and local formulary for individual patient prescribing decisions.
        </p>
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                tab === t.key
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "nice-ta" && <NiceTaSection />}
      {tab === "screening" && <ScreeningSection />}
      {tab === "biosimilar" && <BiosimilarSection />}
      {tab === "monitoring" && <MonitoringSection />}
    </DoctorLayout>
  );
}
